"use client";

import { Container, Typography, Box, Paper, Button, useTheme } from "@mui/material";
import EnrollmentHeader from "@/components/EnrollmentHeader";
import EnrollmentFooter from "@/components/EnrollmentFooter";
import { usePageTitle } from "@/hooks/usePageTitle";
import { resolveStepDisabled, useStudyStatus } from "@/hooks/useStudyStatus";

export default function NadaPortalPage() {
    const theme = useTheme();
    const tenant = "nada";

    usePageTitle("Dealer Portal", tenant);

    const { stepOverrides } = useStudyStatus(tenant);
    const enrollDisabled = resolveStepDisabled(stepOverrides.enroll, true);
    const editParticipantsDisabled = resolveStepDisabled(stepOverrides.edit_participants, true);
    const surveyDisabled = resolveStepDisabled(stepOverrides.survey, true);
    const uploadPayrollDisabled = resolveStepDisabled(stepOverrides.upload_payroll, false);

    return (
        <Box sx={{ bgcolor: "white", minHeight: "100vh" }}>
            <EnrollmentHeader tenant={tenant} />

            {/* Main content */}
            <Container maxWidth="lg" sx={{ py: 4 }} component="main" role="main">
                <Typography variant="h4" component="h1" sx={{ mb: 3, fontWeight: 700 }}>
                    Dealer Portal
                </Typography>

                {/* Step 1: Enroll */}
                <Paper
                    elevation={2}
                    sx={{ mb: 3 }}
                    component="section"
                    aria-labelledby="step1-title"
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "stretch",
                            flexDirection: { xs: "column", md: "row" },
                        }}
                    >
                        <Box sx={{ flex: 1, p: 3 }}>
                            <Typography
                                id="step1-title"
                                variant="h6"
                                component="h2"
                                sx={{ mb: 2, fontWeight: 600, color: theme.palette.primary.main }}
                            >
                                Step 1: Enroll
                            </Typography>
                            <Box component="ul" sx={{ pl: 3, m: 0 }}>
                                <li>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        Look up your dealership using your 8-9 digit NADA Member ID
                                    </Typography>
                                </li>
                                <li>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        Confirm your dealership info and accept the NADA terms of
                                        agreement
                                    </Typography>
                                </li>
                                <li>
                                    <Typography variant="body2">
                                        Receive enrollment confirmation email
                                    </Typography>
                                </li>
                            </Box>
                        </Box>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                p: 3,
                                borderLeft: { md: 1 },
                                borderTop: { xs: 1, md: 0 },
                                borderColor: "divider",
                            }}
                        >
                            <Button
                                variant="contained"
                                size="large"
                                href="/enrollment"
                                aria-label={enrollDisabled ? "Enrollment is closed" : "Go to enrollment"}
                                disabled={enrollDisabled}
                                sx={{ minWidth: 232, height: 40 }}
                            >
                                {enrollDisabled ? "Enrollment Closed" : "Enroll"}
                            </Button>
                        </Box>
                    </Box>
                </Paper>

                {/* Step 2: Confirm Your Participating Dealership Info */}
                <Paper
                    elevation={2}
                    sx={{ mb: 3 }}
                    component="section"
                    aria-labelledby="step2-title"
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "stretch",
                            flexDirection: { xs: "column", md: "row" },
                        }}
                    >
                        <Box sx={{ flex: 1, p: 3 }}>
                            <Typography
                                id="step2-title"
                                variant="h6"
                                component="h2"
                                sx={{ mb: 2, fontWeight: 600, color: theme.palette.primary.main }}
                            >
                                Step 2: Confirm Your Participating Dealership Info
                            </Typography>
                            <Box component="ul" sx={{ pl: 3, m: 0 }}>
                                <li>
                                    <Typography variant="body2">
                                        Add or edit participating dealerships and franchise info
                                    </Typography>
                                </li>
                            </Box>
                        </Box>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                p: 3,
                                borderLeft: { md: 1 },
                                borderTop: { xs: 1, md: 0 },
                                borderColor: "divider",
                            }}
                        >
                            <Button
                                variant="contained"
                                size="large"
                                href="/edit-participants"
                                aria-label={editParticipantsDisabled ? "Edit participants is closed" : "Add or edit dealerships"}
                                disabled={editParticipantsDisabled}
                                sx={{ minWidth: 232, height: 40 }}
                            >
                                Add/Edit Dealerships
                            </Button>
                        </Box>
                    </Box>
                </Paper>

                {/* Step 3: The Questionnaire */}
                <Paper
                    elevation={2}
                    sx={{ mb: 3 }}
                    component="section"
                    aria-labelledby="step3-title"
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "stretch",
                            flexDirection: { xs: "column", md: "row" },
                        }}
                    >
                        <Box sx={{ flex: 1, p: 3 }}>
                            <Typography
                                id="step3-title"
                                variant="h6"
                                component="h2"
                                sx={{ mb: 2, fontWeight: 600, color: theme.palette.primary.main }}
                            >
                                Step 3: The Questionnaire
                            </Typography>
                            <Box component="ul" sx={{ pl: 3, m: 0 }}>
                                <li>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        Complete the online questionnaire with your benefits and
                                        compensation details
                                    </Typography>
                                </li>
                                <li>
                                    <Typography variant="body2">
                                        <a
                                            href="/files/2026-nada-survey-content.pdf"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{
                                                textDecoration: "underline",
                                                color: theme.palette.primary.main,
                                            }}
                                        >
                                            Download the NADA Questionnaire Worksheet (PDF)
                                        </a>
                                    </Typography>
                                </li>
                            </Box>
                        </Box>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                p: 3,
                                borderLeft: { md: 1 },
                                borderTop: { xs: 1, md: 0 },
                                borderColor: "divider",
                            }}
                        >
                            <Button
                                variant="contained"
                                size="large"
                                href="/survey"
                                aria-label={surveyDisabled ? "Questionnaire is closed" : "Start questionnaire"}
                                disabled={surveyDisabled}
                                sx={{ minWidth: 232, height: 40 }}
                            >
                                Start Questionnaire
                            </Button>
                        </Box>
                    </Box>
                </Paper>

                {/* Step 4: Upload Your Payroll File */}
                <Paper
                    elevation={2}
                    sx={{ mb: 3 }}
                    component="section"
                    aria-labelledby="step4-title"
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "stretch",
                            flexDirection: { xs: "column", md: "row" },
                        }}
                    >
                        <Box sx={{ flex: 1, p: 3 }}>
                            <Typography
                                id="step4-title"
                                variant="h6"
                                component="h2"
                                sx={{ mb: 2, fontWeight: 600, color: theme.palette.primary.main }}
                            >
                                Step 4: Upload Your Payroll File
                            </Typography>
                            <Box component="ul" sx={{ pl: 3, m: 0, mb: 2 }}>
                                <li>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        Extract your data from your payroll system
                                    </Typography>
                                </li>
                                <li>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        Complete the payroll template with data for ALL employees
                                    </Typography>
                                </li>
                                <li>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        Include all employees on payroll as of December 31, 2025
                                    </Typography>
                                </li>
                                <li>
                                    <Typography variant="body2">
                                        Upload your completed payroll file
                                    </Typography>
                                </li>
                            </Box>
                            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, pl: 3 }}>
                                Payroll Query Instructions by Provider:
                            </Typography>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, pl: 5, mb: 2 }}>
                                <a
                                    href="/files/2026-nada-payroll-instructions-cdk-global-legacy.pdf"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                        textDecoration: "underline",
                                        color: theme.palette.primary.main,
                                        fontSize: "0.875rem",
                                    }}
                                >
                                    CDK Global Legacy (PDF)
                                </a>
                                <a
                                    href="/files/2026-nada-payroll-instructions-cdk-global-payroll-plus.pdf"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                        textDecoration: "underline",
                                        color: theme.palette.primary.main,
                                        fontSize: "0.875rem",
                                    }}
                                >
                                    CDK Global Payroll Plus (PDF)
                                </a>
                                <a
                                    href="/files/2026-nada-payroll-instructions-dominion-access.pdf"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                        textDecoration: "underline",
                                        color: theme.palette.primary.main,
                                        fontSize: "0.875rem",
                                    }}
                                >
                                    Dominion ACCESS (PDF)
                                </a>
                                <a
                                    href="/files/2026-nada-payroll-instructions-netchex.pdf"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                        textDecoration: "underline",
                                        color: theme.palette.primary.main,
                                        fontSize: "0.875rem",
                                    }}
                                >
                                    Netchex (PDF)
                                </a>
                                <a
                                    href="/files/2026-nada-payroll-instructions-paylocity.pdf"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                        textDecoration: "underline",
                                        color: theme.palette.primary.main,
                                        fontSize: "0.875rem",
                                    }}
                                >
                                    Paylocity (PDF)
                                </a>
                                <a
                                    href="/files/2026-nada-payroll-instructions-reynolds-and-reynolds.pdf"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                        textDecoration: "underline",
                                        color: theme.palette.primary.main,
                                        fontSize: "0.875rem",
                                    }}
                                >
                                    Reynolds & Reynolds (PDF)
                                </a>
                                <a
                                    href="/files/2026-nada-payroll-instructions-ukg-pro.pdf"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                        textDecoration: "underline",
                                        color: theme.palette.primary.main,
                                        fontSize: "0.875rem",
                                    }}
                                >
                                    UKG Pro (PDF)
                                </a>
                            </Box>
                        </Box>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                p: 3,
                                borderLeft: { md: 1 },
                                borderTop: { xs: 1, md: 0 },
                                borderColor: "divider",
                            }}
                        >
                            <Button
                                variant="contained"
                                size="large"
                                href="/upload"
                                aria-label={uploadPayrollDisabled ? "Payroll upload is closed" : "Go to upload page"}
                                disabled={uploadPayrollDisabled}
                                sx={{ minWidth: 232, height: 40 }}
                            >
                                Upload Payroll
                            </Button>
                        </Box>
                    </Box>
                </Paper>

                {/* Closing message */}
                <Typography
                    variant="body1"
                    sx={{ fontWeight: 600, textAlign: "center", my: 4 }}
                    role="alert"
                    aria-live="polite"
                >
                    All data must be submitted by March 27, 2026
                </Typography>

                <EnrollmentFooter tenant={tenant} />
            </Container>
        </Box>
    );
}
